import {useRef, useState} from 'react';

import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import classNames from 'classnames';

import {useIntl} from 'react-intl';

import {fileSizeToString} from '@time-webkit/all/utils/attachments';

import {IMAGE_EXTENSIONS, MAX_IMAGE_SIZE} from './constants';

import {useIsDragging, useValidation} from './hooks';
import styles from './styles.module.css';
import {Loader} from './components/loader';
import {Actions} from './components/actions';

type Props = {
    onFileChange: (file: File) => void;
    onFileDeleted: () => void;
    extensions?: string[];
    maxImageSize?: number;
    minImageWidth?: number;
    minImageHeight?: number;
    disabled?: boolean;
};

export const FormUploadImage: React.FC<Props> = ({
    onFileChange,
    onFileDeleted,
    maxImageSize = MAX_IMAGE_SIZE,
    extensions = IMAGE_EXTENSIONS,
    minImageWidth,
    minImageHeight,
    disabled,
}) => {
    const {formatMessage} = useIntl();
    const isDragging = useIsDragging();
    const validate = useValidation({extensions, maxImageSize, minImageHeight, minImageWidth});

    const [currentFile, setCurrentFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);

    const [bodyM, bodyMTrue, bodyS] = useTypography([
        {
            size: TypographySize.BodyM,
        },
        {
            size: TypographySize.BodyMTrue,
        },
        {
            size: TypographySize.BodyS,
        },
    ]);

    const handleDragzoneClick = () => {
        fileInputRef.current?.click();
    };

    const handleFilesDrop = (e: React.DragEvent<HTMLInputElement>) => {
        const file = e.dataTransfer?.files[0];

        if (file) {
            handleFile(file);
        }
    };

    const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (file) {
            handleFile(file);
        }
    };

    const handleRemoveClick = () => {
        setCurrentFile(null);
        setImageLoaded(false);
        setError(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        onFileDeleted();
    };

    const loadPreview = (file: File) => {
        if (imageRef.current) {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            imageRef.current.onload = () => {
                setImageLoaded(true);
            };

            reader.onloadend = () => {
                if (imageRef.current && typeof reader.result === 'string') {
                    imageRef.current.src = reader.result;
                }
            };
        }
    };

    const handleFile = async (file: File) => {
        const error = await validate(file);

        if (error) {
            setError(error);
            setCurrentFile(null);
            return;
        }

        setCurrentFile(file);
        setError(null);
        setImageLoaded(false);

        loadPreview(file);
        onFileChange(file);
    };

    const initialText = (
        <span>
            <span className={styles.drag_highlight_text}>
                {formatMessage({id: 'form_upload_image.select_photo', defaultMessage: 'Select a photo'})}
            </span>{' '}
            {formatMessage({id: 'form_upload_image.or_drag', defaultMessage: 'or drag it here'})}
        </span>
    );
    const draggingText = formatMessage({id: 'form_upload_image.drag_file', defaultMessage: 'Drag file here'});

    const dragText = isDragging && !disabled ? draggingText : initialText;

    return (
        <div className={styles.container}>
            <div
                className={classNames(currentFile && imageLoaded ? styles.file_info_container : styles.drag_container, {
                    [styles.drag_container_hovered]: isDragging && !disabled,
                    [styles.disabled]: disabled,
                })}
                onDrop={handleFilesDrop}
                onClick={handleDragzoneClick}
            >
                <div className={classNames(styles.file_infos, {[styles.hide]: !currentFile || !imageLoaded})}>
                    <img
                        width='36px'
                        height='36px'
                        ref={imageRef}
                        className={styles.image}
                    />
                    <div className={styles.file_info}>
                        <div className={classNames(bodyMTrue, styles.file_name)}>{currentFile?.name}</div>
                        <div className={classNames(bodyS, styles.size)}>{fileSizeToString(currentFile?.size ?? 0)}</div>
                    </div>
                    {!disabled && (
                        <Actions
                            handleRemoveClick={handleRemoveClick}
                            handleEditClick={handleDragzoneClick}
                        />
                    )}
                </div>

                {!currentFile && <div className={classNames(styles.drag_text, bodyM)}>{dragText}</div>}

                <Loader show={Boolean(currentFile && !imageLoaded)} />

                <input
                    type='file'
                    data-testid='formUploadImageInput'
                    accept={extensions.join(',')}
                    style={{display: 'none'}}
                    onChange={handleFilesChange}
                    ref={fileInputRef}
                />
            </div>
            {error && <div className={classNames(styles.error, bodyM)}>{error}</div>}
        </div>
    );
};
